import Button from '../Button.js';
import Contact from '../Contact.js';
import Icon from '../Icon.js';
import Services from '../Services.js';
import StepFrame from './StepFrame.js';
import Theme from '../styles/Theme.js';
import Utils from '../Utils.js';

/*
 * ContactStep
 *
 * The number, and nothing standing in front of it.
 *
 * No form, no details collected, no "before you call, please confirm". Somebody
 * who has chosen to phone has already decided that typing is not what they want
 * to be doing, and every field between that decision and the number is a field
 * they will resent. The number is the largest thing on the screen and it dials
 * on a tap.
 *
 * WRITING IN IS STILL OFFERED UNDERNEATH, because the two are not equivalent
 * and the difference is worth knowing: the form carries every answer they have
 * already given, and a phone call starts from nothing. Anybody six questions
 * deep should hear that before they abandon the six questions.
 */
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
const ContactStep = ({
  brand,
  lookup,
  node,
  onContinue,
  stepKey
}) => {
  const mobile = Utils.isMobile();
  const target = Contact.forBrand(brand, lookup);
  if (!target) {
    return null;
  }

  /*
   * A dealership is either the whole context or absent, and the copy has to
   * know which.
   *
   * On a dealership's own website there is no ambiguity to resolve and no
   * account to look anybody up in: this is their dealership, on their dealership's
   * site, and saying "the dealership on your account" reads as though we had to
   * go and check. On the Support Center a dealership only appears because a
   * lookup found one, and there naming the account is exactly right.
   */
  const owner = Services.dealership();
  const dealership = target.role === 'dealership';
  const subtitle = () => {
    if (dealership && owner?.id) {
      return target.hours || 'Speak to the people who installed your system';
    }
    if (dealership) {
      return target.hours || `${target.name}, the dealership on your account`;
    }
    return target.hours;
  };
  return /*#__PURE__*/_jsxs(StepFrame, {
    eyebrow: 'Call us',
    stepKey: stepKey,
    title: dealership ? `Call ${target.name}` : 'Give us a call',
    children: [/*#__PURE__*/_jsxs("a", {
      href: `tel:${Contact.dialable(target.phone_number)}`,
      style: {
        alignItems: 'center',
        border: `1px solid ${Theme.colors.border()}`,
        borderRadius: 16,
        display: 'flex',
        gap: mobile ? 16 : 20,
        padding: mobile ? '22px 20px' : '28px 30px',
        /* Opts out of the global underline. It is a card with a border,
           an icon and a heading sized number; underlining every line
           inside it marks nothing and reads as damage. */
        textDecoration: 'none'
      },
      children: [/*#__PURE__*/_jsx("span", {
        style: {
          alignItems: 'center',
          backgroundColor: Theme.tint(0.1),
          borderRadius: 999,
          display: 'inline-flex',
          flexShrink: 0,
          height: mobile ? 46 : 54,
          justifyContent: 'center',
          width: mobile ? 46 : 54
        },
        children: /*#__PURE__*/_jsx(Icon, {
          color: Theme.colors.primary(),
          name: 'telephone-fill',
          size: mobile ? 19 : 22
        })
      }), /*#__PURE__*/_jsxs("span", {
        style: {
          minWidth: 0
        },
        children: [/*#__PURE__*/_jsx("span", {
          style: {
            ...Theme.textStyles.heading({
              fontSize: mobile ? 24 : 30
            }),
            color: Theme.colors.primary(),
            display: 'block',
            fontVariantNumeric: 'tabular-nums'
          },
          children: Utils.formatPhoneNumber(target.phone_number)
        }), /*#__PURE__*/_jsx("span", {
          style: {
            ...Theme.textStyles.body({
              fontSize: 14
            }),
            display: 'block',
            marginTop: 5
          },
          children: subtitle()
        })]
      })]
    }), /*#__PURE__*/_jsx("p", {
      style: {
        ...Theme.textStyles.body({
          fontSize: 14,
          lineHeight: 1.65
        }),
        marginTop: mobile ? 22 : 26
      },
      children: owner?.id ? `Worth knowing before you dial: a call starts from a blank page. Everything you have told us so far stays here, so you will be going through it again with whoever picks up at ${owner.name}.` : 'Worth knowing before you dial: a call starts from a blank page. Everything you have told us so far stays here, so you will be going through it again out loud.'
    }), /*#__PURE__*/_jsxs("div", {
      style: {
        borderTop: `1px solid ${Theme.colors.border()}`,
        marginTop: mobile ? 22 : 26,
        paddingTop: mobile ? 20 : 24
      },
      children: [/*#__PURE__*/_jsx("span", {
        style: {
          ...Theme.textStyles.subHeading({
            fontSize: mobile ? 16 : 17
          }),
          display: 'block',
          marginBottom: 6
        },
        children: 'Or send it in writing'
      }), /*#__PURE__*/_jsx("span", {
        style: {
          ...Theme.textStyles.body({
            fontSize: 14
          }),
          display: 'block',
          marginBottom: 18
        },
        children: owner?.id ? `Your answers go with it, and ${owner.name} picks it up already knowing what you have tried.` : 'Your answers go with it, and somebody picks it up knowing what you have already tried.'
      }), /*#__PURE__*/_jsx(Button, {
        icon: 'envelope-fill',
        label: 'Send it in writing instead',
        onClick: onContinue.bind(this, {
          next: node.next
        }),
        type: 'outline'
      })]
    })]
  });
};
export default ContactStep;